import React, { useEffect, useState, useCallback } from 'react';
import { getAdminTestimonials, updateTestimonialStatus } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Star, Filter } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const TestimonialsManagement = () => {
  const { t } = useLanguage();
  const statusOptions = [
    { value: 'all', label: t('portalUi.admin.testimonials.filters.all', 'Visas') },
    { value: 'pending', label: t('portalUi.admin.testimonials.filters.pending', 'Gaida apstiprinājumu') },
    { value: 'approved', label: t('portalUi.admin.testimonials.filters.approved', 'Publicētas') },
    { value: 'hidden', label: t('portalUi.admin.testimonials.filters.hidden', 'Paslēptas') }
  ];
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchTestimonials = useCallback(async () => {
    try {
      const data = await getAdminTestimonials(statusFilter === 'all' ? null : statusFilter);
      setTestimonials(data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const handleStatusChange = async (testimonialId, status) => {
    try {
      await updateTestimonialStatus(testimonialId, status);
      fetchTestimonials();
    } catch (error) {
      console.error('Failed to update testimonial:', error);
    }
  };

  const renderStars = (value) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={`admin-star-${index}`}
        className={`w-4 h-4 ${index < value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`}
      />
    ));
  };

  return (
    <div className="space-y-6" data-testid="admin-testimonials">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2E2E2E]" data-testid="admin-testimonials-title">
            {t('portalUi.admin.testimonials.title', 'Atsauksmju pārvaldība')}
          </h1>
          <p className="text-[#2E2E2E]/60" data-testid="admin-testimonials-subtitle">
            {t('portalUi.admin.testimonials.subtitle', 'Apstipriniet vai paslēpiet klientu atsauksmes, pirms tās parādās mājaslapā.')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-[#1F3D2B]" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-[#1F3D2B]/20 rounded-lg"
            data-testid="admin-testimonials-filter"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="forest-panel rounded-xl p-6 text-[#2E2E2E]/60">{t('portalUi.admin.testimonials.loading', 'Ielādē atsauksmes...')}</div>
      ) : testimonials.length === 0 ? (
        <div className="forest-panel rounded-xl p-6 text-[#2E2E2E]/60" data-testid="admin-testimonials-empty">
          {t('portalUi.admin.testimonials.empty', 'Šobrīd nav atsauksmju, kas atbilst izvēlētajam filtram.')}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-testimonials-list">
          {testimonials.map((testimonial) => {
            const average = Math.round(
              (testimonial.rating + testimonial.speed + testimonial.quality + testimonial.communication + testimonial.responsiveness) / 5
            );
            return (
              <Card key={testimonial.id} className="border-0 shadow-sm forest-panel card-shine">
                <CardHeader>
                  <CardTitle className="text-lg text-[#1F3D2B]">
                    {testimonial.name_or_company}
                  </CardTitle>
                  <p className="text-sm text-[#2E2E2E]/60">{testimonial.region}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-1">
                    {renderStars(average)}
                    <span className="text-sm text-[#2E2E2E]/60 ml-2">{t('portalUi.admin.testimonials.average', 'Vidēji {rating}/5').replace('{rating}', average)}</span>
                  </div>
                  <p className="text-sm text-[#2E2E2E]/70">{testimonial.text}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-[#2E2E2E]/60">
                    <div>{t('portalUi.admin.testimonials.speed', 'Ātrums')}: {testimonial.speed}/5</div>
                    <div>{t('portalUi.admin.testimonials.quality', 'Kvalitāte')}: {testimonial.quality}/5</div>
                    <div>{t('portalUi.admin.testimonials.communication', 'Komunikācija')}: {testimonial.communication}/5</div>
                    <div>{t('portalUi.admin.testimonials.responsiveness', 'Atsaucība')}: {testimonial.responsiveness}/5</div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2">
                    {testimonial.status !== 'approved' && (
                      <Button
                        size="sm"
                        className="forest-cta"
                        onClick={() => handleStatusChange(testimonial.id, 'approved')}
                        data-testid={`admin-testimonials-approve-${testimonial.id}`}
                      >
                        {t('portalUi.admin.testimonials.publish', 'Publicēt')}
                      </Button>
                    )}
                    {testimonial.status !== 'hidden' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="forest-cta-outline"
                        onClick={() => handleStatusChange(testimonial.id, 'hidden')}
                        data-testid={`admin-testimonials-hide-${testimonial.id}`}
                      >
                        {t('portalUi.admin.testimonials.hide', 'Paslēpt')}
                      </Button>
                    )}
                    {testimonial.status !== 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#1F3D2B]/20 text-[#1F3D2B]"
                        onClick={() => handleStatusChange(testimonial.id, 'pending')}
                        data-testid={`admin-testimonials-pending-${testimonial.id}`}
                      >
                        {t('portalUi.admin.testimonials.returnToPending', 'Atgriezt uz gaidīšanu')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TestimonialsManagement;
